import { LeapV2StudentProgress } from './models/leap-v2-student.model';

// Single source of truth for the "field has been filled in" rules, shared by the parent Submit gate
// (TutorReportLeapV2Component.reportComplete) and the per-field inline hints in the student panel.
// The sentinels mirror the score controls: attainment is -1 until picked (leap-v2-attainment-bar),
// engagement is 0 until picked (leap-v2-engagement-bar).

export function isAttainmentSet(level: number | undefined | null): boolean {
  return typeof level === 'number' && level >= 0;
}

export function isEngagementSet(value: number | undefined | null): boolean {
  return typeof value === 'number' && value > 0;
}

export function hasText(text: string | undefined | null): boolean {
  return (text ?? '').trim().length > 0;
}

/**
 * Attainment holds one score per learning objective, so its array must track the objective count: pad
 * added objectives with the "not picked" sentinel (-1) and drop entries for removed ones. Shared by the
 * student panel (which aligns as it loads) and the parent's unsaved-changes baseline capture, so the two
 * agree on the normalised shape and this alignment is never itself read as an unsaved edit.
 */
export function alignAttainmentLevels(progress: LeapV2StudentProgress, objectiveCount: number): void {
  const levels = progress.attainmentLevels;
  while (levels.length < objectiveCount) levels.push(-1);
  if (levels.length > objectiveCount) levels.length = objectiveCount;
}

/**
 * A student's session report is complete when every learning objective has an attainment score and
 * both comment fields plus the engagement score are filled in. Evidence of work and behavioural
 * concerns are optional and deliberately not checked here.
 */
export function isStudentProgressComplete(progress: LeapV2StudentProgress, objectiveCount: number): boolean {
  const attainmentComplete = Array.from({ length: objectiveCount })
    .every((_, i) => isAttainmentSet(progress.attainmentLevels[i]));

  return attainmentComplete
    && hasText(progress.objectiveComments)
    && isEngagementSet(progress.engagement)
    && hasText(progress.engagementComments);
}
